import { useEffect } from 'react';

/**
 * Radix UI's Dialog/Popover primitives sometimes leave `body { pointer-events: none }`
 * as an inline style when a dismissable layer closes while another portalled layer
 * is still mounted (open={false} is still mounted). When that happens the page
 * silently stops responding to clicks — the carousel, thumbnails, "Manage Listing"
 * button, etc. all appear inert.
 *
 * This guard watches the <body> for that inline `pointer-events: none` and,
 * if there are no actually-open Radix dismissable layers, clears it on the
 * next microtask. Cheap, safe, and idempotent.
 */
export function useBodyPointerEventsGuard() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;

    const hasOpenLayer = () =>
      !!document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-radix-popper-content-wrapper] [data-state="open"]'
      );

    const unstick = () => {
      if (body.style.pointerEvents === 'none' && !hasOpenLayer()) {
        body.style.pointerEvents = '';
      }
    };

    const obs = new MutationObserver(() => {
      // Defer to let Radix finish its own cleanup first.
      queueMicrotask(unstick);
    });
    obs.observe(body, { attributes: true, attributeFilter: ['style'] });
    // Also observe descendant open-state changes.
    obs.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    unstick();
    return () => obs.disconnect();
  }, []);
}
